import { Box, Divider, Heading, HStack, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { ProtocolBlockDto, ProtocolSectionDto } from "../../../../autogen/bff-api";
import { NarrativeField } from "./NarrativeField";

const FieldBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <HStack align="baseline" spacing="3" py="1">
    <Text fontSize="sm" color="smMuted" minW="220px">
      {block.label}
    </Text>
    <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
      {block.value || "–"}
    </Text>
  </HStack>
);

const NoteBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <Box py="1">
    {block.label && (
      <Text fontSize="sm" color="smMuted" mb="1">
        {block.label}
      </Text>
    )}
    <Text fontSize="sm" fontStyle="italic" color="smMuted" whiteSpace="pre-wrap">
      {block.value}
    </Text>
  </Box>
);

const SubheadingBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <Text fontSize="13px" fontWeight="600" color="smPrimary" mt="14px" mb="1">
    {block.value}
  </Text>
);

const TableBlock = ({ block }: { block: ProtocolBlockDto }) => {
  const columns = block.columns ?? [];
  const rows = block.rows ?? [];
  const numericColumns = new Set(block.numericColumns ?? []);
  const emphasizeFirstRow = block.emphasizeFirstRow ?? false;

  const headerCellProps = {
    color: "smMuted",
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.03em",
    fontWeight: "600",
    borderBottom: "1.5px solid",
    borderColor: "smBorder",
    px: "8px",
    pt: "4px",
    pb: "6px",
  };

  return (
    <Box overflowX="auto" py="1">
      <Table variant="unstyled" width="100%">
        <Thead>
          <Tr>
            {columns.map((col, colIndex) => (
              <Th
                key={col}
                {...headerCellProps}
                textAlign={numericColumns.has(colIndex) ? "right" : "left"}
              >
                {col}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.length === 0 && block.emptyText ? (
            <Tr>
              <Td
                colSpan={Math.max(columns.length, 1)}
                textAlign="center"
                fontStyle="italic"
                color="smMuted"
                fontSize="sm"
                py="3"
                px="8px"
              >
                {block.emptyText}
              </Td>
            </Tr>
          ) : (
            rows.map((row, rowIndex) => {
              const isWinner = emphasizeFirstRow && rowIndex === 0;
              const isAlt = rowIndex % 2 === 0;
              const rowBg = isAlt ? "smProtocolRowAltBg" : undefined;
              return (
                <Tr key={rowIndex} bg={rowBg}>
                  {row.map((cell, cellIndex) => (
                    <Td
                      key={cellIndex}
                      color="smText"
                      fontWeight={isWinner ? "600" : undefined}
                      borderBottom="1px solid"
                      borderColor="smBorder"
                      px="8px"
                      py="6px"
                      textAlign={numericColumns.has(cellIndex) ? "right" : "left"}
                    >
                      <Text fontSize="sm">{cell || "–"}</Text>
                    </Td>
                  ))}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

interface Props {
  section: ProtocolSectionDto;
  onSaveNarrative: (editableKey: string, value: string) => void;
}

export const ProtocolSectionView = ({ section, onSaveNarrative }: Props) => {
  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Heading as="h2" size="sm" color="smText" mb="1">
        {section.number}. {section.heading}
      </Heading>
      <Divider mb="3" borderColor="smBorder" />
      <Stack spacing="2">
        {section.blocks.map((block, index) => {
          if (block.kind === "paragraph" && block.editableKey) {
            return (
              <Box key={index} py="1">
                {block.label && (
                  <Text fontSize="sm" color="smMuted" mb="1">
                    {block.label}
                  </Text>
                )}
                <NarrativeField
                  initialValue={block.value ?? ""}
                  onSave={(value) => onSaveNarrative(block.editableKey as string, value)}
                />
              </Box>
            );
          }
          if (block.kind === "paragraph") return <NoteBlock key={index} block={block} />;
          if (block.kind === "note") return <NoteBlock key={index} block={block} />;
          if (block.kind === "subheading") return <SubheadingBlock key={index} block={block} />;
          if (block.kind === "table") return <TableBlock key={index} block={block} />;
          return <FieldBlock key={index} block={block} />;
        })}
      </Stack>
    </Box>
  );
};
